import React, { useMemo } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import type { PineParseWarning } from "../../types/reportStrategyPine";
import { groupWarningsByCode } from "../../utils/pineIrUi";

export type StrategyPineParseWarningsProps = {
  warnings: PineParseWarning[] | undefined;
  subtleText: string;
  groupByCode?: boolean;
  onGoToLine?: (line: number) => void;
  warningCodeLabels?: Record<string, { title?: string; message?: string }>;
};

export function StrategyPineParseWarnings({
  warnings,
  subtleText,
  groupByCode = true,
  onGoToLine,
  warningCodeLabels,
}: StrategyPineParseWarningsProps): React.ReactElement | null {
  const grouped = useMemo(() => {
    if (!warnings?.length) return null;
    return groupByCode ? groupWarningsByCode(warnings) : null;
  }, [groupByCode, warnings]);

  if (!warnings?.length) return null;

  const renderWarning = (w: PineParseWarning, idx: number) => (
    <ListItem key={`${w.code}-${idx}`}>
      <Text as="span" fontFamily="mono" fontSize="xs" mr={2}>
        {w.code}
      </Text>
      {warningCodeLabels?.[w.code]?.title ? (
        <Text as="span" fontWeight="medium" mr={1}>
          {warningCodeLabels[w.code].title}:
        </Text>
      ) : null}
      {w.message}
      {typeof w.line === "number" ? (
        <>
          {" "}
          {onGoToLine ? (
            <Button
              size="xs"
              variant="link"
              colorScheme="cyan"
              onClick={() => onGoToLine(w.line!)}
            >
              line {w.line}
            </Button>
          ) : (
            `(line ${w.line})`
          )}
        </>
      ) : null}
    </ListItem>
  );

  return (
    <Alert status="warning" borderRadius="8px" alignItems="flex-start">
      <AlertIcon mt={1} />
      <AlertDescription w="100%">
        <Text fontSize="sm" fontWeight="medium" mb={2}>
          Parse warnings
        </Text>
        {grouped ? (
          <Box>
            {Array.from(grouped.entries()).map(([code, items]) => (
              <Box key={code} mb={2}>
                <Text fontSize="xs" fontFamily="mono" fontWeight="semibold" color={subtleText} mb={1}>
                  {code}
                  {warningCodeLabels?.[code]?.title ? ` — ${warningCodeLabels[code].title}` : ""}
                </Text>
                <UnorderedList spacing={1} fontSize="sm" color={subtleText} pl={4}>
                  {items.map((w, idx) => renderWarning(w, idx))}
                </UnorderedList>
              </Box>
            ))}
          </Box>
        ) : (
          <UnorderedList spacing={1} fontSize="sm" color={subtleText}>
            {warnings.map((w, idx) => renderWarning(w, idx))}
          </UnorderedList>
        )}
      </AlertDescription>
    </Alert>
  );
}
